import PathNames from "@app/const/PathNames"
import MenyNativeEvent from "@app/events/MenyNativeEvent"
import { useShouldUseNativeTabBar } from "@app/helpers/nativeTabBar/useShouldUseNativeTabBar"
import { RootState } from "@app/types"
import { NativeEventBridge } from "@ng-mw/shared-react-components"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useLocation } from "react-router-dom"

const overlaySelectors = [
    "dialog.ngr-modal[open]",
    '[role="dialog"][aria-modal="true"]',
    ".ngr-drawer--open",
]

const hasBlockingDomOverlay = (): boolean =>
    overlaySelectors.some(selector =>
        Array.from(document.body.querySelectorAll(selector)).some(
            element => !element.closest('[aria-hidden="true"], [hidden]'),
        ),
    )

export const NativeTabBarOverlayGuard = () => {
    const { pathname } = useLocation()
    const supportsNativeTabs = useSelector(
        (state: RootState) => state.appEnvConfig.supportsNativeTabs,
    )

    const activePopupCount = useSelector((state: RootState) => state.app.activePopups.length)
    const [hasDomOverlay, setHasDomOverlay] = useState(false)
    const [lastVisibleState, setLastVisibleState] = useState<boolean | null>(null)
    const shouldUseNativeTabBar = useShouldUseNativeTabBar(supportsNativeTabs)
    const isCheckout = pathname.startsWith(PathNames.Checkout)
    const hasFullscreenPopup = useSelector((state: RootState) =>
        state.app.activePopups.some(popup => popup.isFullscreenPopup),
    )
    useEffect(() => {
        if (
            !shouldUseNativeTabBar ||
            typeof MutationObserver === "undefined" ||
            typeof document === "undefined" ||
            !document.body
        ) {
            setHasDomOverlay(false)
            return undefined
        }

        const updateHasDomOverlay = () => setHasDomOverlay(hasBlockingDomOverlay())
        const observer = new MutationObserver(updateHasDomOverlay)
        updateHasDomOverlay()
        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ["aria-hidden", "aria-modal", "class", "hidden", "open", "style"],
            childList: true,
            subtree: true,
        })

        return () => observer.disconnect()
    }, [shouldUseNativeTabBar])

    useEffect(() => {
        const shouldRestoreVisibility = lastVisibleState === false

        return () => {
            if (shouldRestoreVisibility) {
                NativeEventBridge.broadcastNative(MenyNativeEvent.TabBarVisibility, {
                    isVisible: true,
                })
            }
        }
    }, [lastVisibleState])

    useEffect(() => {
        if (!shouldUseNativeTabBar) {
            if (lastVisibleState === false) {
                NativeEventBridge.broadcastNative(MenyNativeEvent.TabBarVisibility, {
                    isVisible: true,
                })
            }

            setLastVisibleState(null)
            return
        }

        const shouldHideTabBar = hasDomOverlay || isCheckout || hasFullscreenPopup
        const isVisible = !shouldHideTabBar

        if (lastVisibleState === isVisible) {
            return
        }

        NativeEventBridge.broadcastNative(MenyNativeEvent.TabBarVisibility, {
            isVisible,
        })
        setLastVisibleState(isVisible)
    }, [activePopupCount, hasDomOverlay, isCheckout, lastVisibleState, shouldUseNativeTabBar])

    return null
}

export default NativeTabBarOverlayGuard
